import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, IndianRupee, Receipt, Clock, TrendingUp, AlertTriangle,
  RefreshCw, Building2, Users, CheckCircle, XCircle, ChevronRight,
  FileText, Calendar, CreditCard, AlertCircle, Download, Upload,
  Search, Filter, MoreHorizontal, Eye, Edit, Banknote, CheckSquare,
  Square, ChevronLeft, ChevronDown, History
} from 'lucide-react';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';

// Invoice Status Colors
const INVOICE_STATUS_STYLES = {
  'Not Eligible': 'bg-slate-100 text-slate-600',
  'Eligible': 'bg-blue-100 text-blue-700',
  'Drafted': 'bg-amber-100 text-amber-700',
  'Sent': 'bg-emerald-100 text-emerald-700'
};

// Collection Status Colors
const COLLECTION_STATUS_STYLES = {
  'Not Started': 'bg-slate-100 text-slate-600',
  'Committed': 'bg-blue-100 text-blue-700',
  'Partially Collected': 'bg-amber-100 text-amber-700',
  'Fully Collected': 'bg-emerald-100 text-emerald-700',
  'Disputed': 'bg-red-100 text-red-700'
};

// Risk Level Colors
const RISK_COLORS = {
  'High': 'text-red-600 bg-red-50',
  'Medium': 'text-amber-600 bg-amber-50',
  'Low': 'text-emerald-600 bg-emerald-50'
};

const BDRevenuePipeline = ({ user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [period, setPeriod] = useState('mtd');
  
  // Data states
  const [summary, setSummary] = useState(null);
  const [aging, setAging] = useState(null);
  const [performance, setPerformance] = useState(null);
  const [alerts, setAlerts] = useState(null);
  
  // Revenue Cases Tab states
  const [allCases, setAllCases] = useState([]);
  const [casesTotal, setCasesTotal] = useState(0);
  const [casesLoading, setCasesLoading] = useState(false);
  const [casesPage, setCasesPage] = useState(0);
  const [casesLimit] = useState(25);
  const [casesSearch, setCasesSearch] = useState('');
  const [casesInvoiceFilter, setCasesInvoiceFilter] = useState('');
  const [casesCollectionFilter, setCasesCollectionFilter] = useState('');
  const [casesAgingFilter, setCasesAgingFilter] = useState('');
  const [selectedCaseIds, setSelectedCaseIds] = useState([]);
  
  // Builder cases modal
  const [selectedBuilder, setSelectedBuilder] = useState(null);
  const [builderCases, setBuilderCases] = useState(null);
  
  // Case detail modal
  const [caseDetailOpen, setCaseDetailOpen] = useState(false);
  const [caseDetail, setCaseDetail] = useState(null);
  const [caseDetailLoading, setCaseDetailLoading] = useState(false);
  
  // Stage update modal
  const [selectedCase, setSelectedCase] = useState(null);
  const [stageUpdateOpen, setStageUpdateOpen] = useState(false);
  const [stageUpdateData, setStageUpdateData] = useState({});
  
  // Partial payment modal
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({ amount: '', payment_mode: '', payment_reference: '', payment_date: '', remark: '' });
  
  // Bulk update modal
  const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
  const [bulkUpdateData, setBulkUpdateData] = useState({});
  const [bulkUpdateLoading, setBulkUpdateLoading] = useState(false);
  
  // Backfill
  const [backfillLoading, setBackfillLoading] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchSummary();
    }
  }, [period]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchSummary(),
        fetchAging(),
        fetchPerformance(),
        fetchAlerts()
      ]);
    } catch (error) {
      console.error('Failed to load revenue data:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have access to Revenue Pipeline');
        navigate('/bd');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await api.getBDRevenueSummary(period);
      setSummary(res.data);
    } catch (error) {
      console.error('Failed to load summary:', error);
      if (error.response?.status === 403) throw error;
    }
  };

  const fetchAging = async () => {
    try {
      const res = await api.getBDRevenueAging();
      setAging(res.data);
    } catch (error) {
      console.error('Failed to load aging:', error);
    }
  };

  const fetchPerformance = async () => {
    try {
      const res = await api.getBDRevenuePerformance();
      setPerformance(res.data);
    } catch (error) {
      console.error('Failed to load performance:', error);
    }
  };

  const fetchAlerts = async () => {
    try {
      const res = await api.getBDRevenueAlerts();
      setAlerts(res.data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const fetchBuilderCases = async (builderId, builderName) => {
    setCasesLoading(true);
    setSelectedBuilder({ builder_id: builderId, builder_name: builderName });
    try {
      const res = await api.getBDRevenueBuilderCases(builderId);
      setBuilderCases(res.data);
    } catch (error) {
      console.error('Failed to load builder cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setCasesLoading(false);
    }
  };

  const handleStageUpdate = async () => {
    if (!selectedCase) return;
    
    try {
      await api.updateBDRevenueCaseStage(selectedCase.case_id, stageUpdateData);
      toast.success('Case updated successfully');
      setStageUpdateOpen(false);
      setSelectedCase(null);
      setStageUpdateData({});
      
      // Refresh data
      if (selectedBuilder) {
        fetchBuilderCases(selectedBuilder.builder_id, selectedBuilder.builder_name);
      }
      fetchAging();
      fetchSummary();
    } catch (error) {
      console.error('Failed to update case:', error);
      toast.error(error.response?.data?.detail || 'Failed to update case');
    }
  };

  const handleBackfill = async () => {
    setBackfillLoading(true);
    try {
      const res = await api.backfillBrokerageCases();
      toast.success(`Backfill complete: ${res.data.created} cases created, ${res.data.skipped_existing} skipped`);
      fetchAllData();
      fetchAllCases();
    } catch (error) {
      console.error('Backfill failed:', error);
      toast.error(error.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfillLoading(false);
    }
  };

  // ==================== Revenue Cases Tab Functions ====================
  
  const fetchAllCases = async (page = casesPage) => {
    setCasesLoading(true);
    try {
      const params = {
        skip: page * casesLimit,
        limit: casesLimit,
        sort_by: 'created_at',
        sort_order: 'desc'
      };
      if (casesSearch) params.search = casesSearch;
      if (casesInvoiceFilter && casesInvoiceFilter !== 'all') params.invoice_status = casesInvoiceFilter;
      if (casesCollectionFilter && casesCollectionFilter !== 'all') params.collection_status = casesCollectionFilter;
      if (casesAgingFilter && casesAgingFilter !== 'all') params.aging_bucket = casesAgingFilter;
      
      const res = await api.getAllBrokerageCases(params);
      setAllCases(res.data.cases || []);
      setCasesTotal(res.data.total || 0);
    } catch (error) {
      console.error('Failed to load cases:', error);
      toast.error('Failed to load cases');
    } finally {
      setCasesLoading(false);
    }
  };
  
  useEffect(() => {
    if (activeTab === 'cases') {
      fetchAllCases(0);
      setCasesPage(0);
    }
  }, [activeTab, casesInvoiceFilter, casesCollectionFilter, casesAgingFilter]);
  
  const handleCasesSearch = (e) => {
    e.preventDefault();
    setCasesPage(0);
    fetchAllCases(0);
  };
  
  const handleCaseSelect = (caseId) => {
    setSelectedCaseIds(prev => 
      prev.includes(caseId) 
        ? prev.filter(id => id !== caseId)
        : [...prev, caseId]
    );
  };
  
  const handleSelectAll = () => {
    if (selectedCaseIds.length === allCases.length) {
      setSelectedCaseIds([]);
    } else {
      setSelectedCaseIds(allCases.map(c => c.case_id));
    }
  };
  
  const openCaseDetail = async (caseId) => {
    setCaseDetailLoading(true);
    setCaseDetailOpen(true);
    try {
      const res = await api.getBrokerageCaseWithHistory(caseId);
      setCaseDetail(res.data);
    } catch (error) {
      console.error('Failed to load case detail:', error);
      toast.error('Failed to load case details');
    } finally {
      setCaseDetailLoading(false);
    }
  };
  
  const handleBulkUpdate = async () => {
    if (selectedCaseIds.length === 0) {
      toast.error('No cases selected');
      return;
    }
    
    setBulkUpdateLoading(true);
    try {
      const payload = {
        case_ids: selectedCaseIds,
        ...bulkUpdateData
      };
      await api.bulkUpdateBrokerageCases(payload);
      toast.success(`${selectedCaseIds.length} cases updated successfully`);
      setBulkUpdateOpen(false);
      setBulkUpdateData({});
      setSelectedCaseIds([]);
      fetchAllCases();
      fetchSummary();
      fetchAging();
    } catch (error) {
      console.error('Bulk update failed:', error);
      toast.error(error.response?.data?.detail || 'Bulk update failed');
    } finally {
      setBulkUpdateLoading(false);
    }
  };
  
  const handlePartialPayment = async () => {
    if (!selectedCase || !paymentData.amount) {
      toast.error('Please fill all required fields');
      return;
    }
    
    try {
      const res = await api.addPartialPayment(selectedCase.case_id, {
        amount: parseFloat(paymentData.amount),
        payment_mode: paymentData.payment_mode,
        payment_reference: paymentData.payment_reference,
        payment_date: paymentData.payment_date,
        remark: paymentData.remark
      });
      toast.success(`Payment of ₹${parseFloat(paymentData.amount).toLocaleString()} recorded. Outstanding: ${formatCurrency(res.data.new_outstanding)}`);
      setPaymentModalOpen(false);
      setPaymentData({ amount: '', payment_mode: '', payment_reference: '', payment_date: '', remark: '' });
      setSelectedCase(null);
      fetchAllCases();
      fetchSummary();
      fetchAging();
    } catch (error) {
      console.error('Payment failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '₹0';
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}K`;
    return `₹${amount?.toFixed(0) || 0}`;
  };

  const getAgingColor = (days) => {
    if (days > 90) return 'text-red-600';
    if (days > 60) return 'text-orange-600';
    if (days > 30) return 'text-amber-600';
    return 'text-emerald-600';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="revenue-pipeline-loading">
        <RefreshCw className="animate-spin text-purple-600" size={32} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="bd-revenue-pipeline-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white px-4 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/bd')}
                className="text-white hover:bg-white/20"
                data-testid="revenue-back-btn"
              >
                <ArrowLeft size={18} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <IndianRupee size={28} />
                  Revenue Pipeline
                </h1>
                <p className="text-emerald-100 text-sm mt-1">Brokerage Collection Lifecycle</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={fetchAllData}
                variant="secondary"
                size="sm"
                className="bg-white/20 hover:bg-white/30 text-white border-0"
                data-testid="revenue-refresh-btn"
              >
                <RefreshCw size={16} className="mr-1" />
                Refresh
              </Button>
              {user?.role === 'super_admin' && (
                <Button
                  onClick={handleBackfill}
                  disabled={backfillLoading}
                  variant="secondary"
                  size="sm"
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                  data-testid="revenue-backfill-btn"
                >
                  {backfillLoading ? (
                    <RefreshCw size={16} className="mr-1 animate-spin" />
                  ) : (
                    <Upload size={16} className="mr-1" />
                  )}
                  Backfill Cases
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6" data-testid="revenue-tabs">
            <TabsTrigger value="dashboard" className="flex items-center gap-2">
              <TrendingUp size={16} />
              Dashboard
            </TabsTrigger>
            <TabsTrigger value="cases" className="flex items-center gap-2">
              <FileText size={16} />
              Revenue Cases
            </TabsTrigger>
            <TabsTrigger value="aging" className="flex items-center gap-2">
              <Clock size={16} />
              Builder Aging
            </TabsTrigger>
            <TabsTrigger value="performance" className="flex items-center gap-2">
              <Users size={16} />
              BD Performance
            </TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" data-testid="revenue-dashboard-tab">
            <div className="space-y-6">
              {/* Period Selector */}
              <div className="flex items-center gap-4">
                <Select value={period} onValueChange={setPeriod}>
                  <SelectTrigger className="w-40" data-testid="revenue-period-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mtd">Month to Date</SelectItem>
                    <SelectItem value="qtd">Quarter to Date</SelectItem>
                    <SelectItem value="fytd">FY to Date</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-slate-500">
                  {summary?.total_cases || 0} total cases
                </span>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className="p-4 bg-blue-50 border-blue-200" data-testid="summary-eligible">
                  <div className="flex items-center gap-2 mb-2">
                    <FileText size={18} className="text-blue-600" />
                    <span className="text-sm text-blue-600 font-medium">Eligible</span>
                  </div>
                  <p className="text-2xl font-bold text-blue-700">
                    {formatCurrency(summary?.total_eligible || 0)}
                  </p>
                </Card>
                
                <Card className="p-4 bg-amber-50 border-amber-200" data-testid="summary-invoiced">
                  <div className="flex items-center gap-2 mb-2">
                    <Receipt size={18} className="text-amber-600" />
                    <span className="text-sm text-amber-600 font-medium">Invoiced</span>
                  </div>
                  <p className="text-2xl font-bold text-amber-700">
                    {formatCurrency(summary?.total_invoiced || 0)}
                  </p>
                </Card>
                
                <Card className="p-4 bg-emerald-50 border-emerald-200" data-testid="summary-collected">
                  <div className="flex items-center gap-2 mb-2">
                    <CheckCircle size={18} className="text-emerald-600" />
                    <span className="text-sm text-emerald-600 font-medium">Collected</span>
                  </div>
                  <p className="text-2xl font-bold text-emerald-700">
                    {formatCurrency(summary?.total_collected || 0)}
                  </p>
                </Card>
                
                <Card className="p-4 bg-red-50 border-red-200" data-testid="summary-outstanding">
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle size={18} className="text-red-600" />
                    <span className="text-sm text-red-600 font-medium">Outstanding</span>
                  </div>
                  <p className="text-2xl font-bold text-red-700">
                    {formatCurrency(summary?.total_outstanding || 0)}
                  </p>
                </Card>
                
                <Card className="p-4 bg-purple-50 border-purple-200" data-testid="summary-efficiency">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp size={18} className="text-purple-600" />
                    <span className="text-sm text-purple-600 font-medium">Efficiency</span>
                  </div>
                  <p className="text-2xl font-bold text-purple-700">
                    {summary?.collection_efficiency_pct || 0}%
                  </p>
                </Card>
              </div>

              {/* Alerts */}
              {alerts?.alerts?.length > 0 && (
                <Card className="overflow-hidden" data-testid="revenue-alerts">
                  <div className="p-4 border-b bg-amber-50">
                    <div className="flex items-center gap-2">
                      <AlertTriangle size={18} className="text-amber-600" />
                      <h3 className="font-semibold text-amber-800">
                        Intelligence Alerts ({alerts.total_alerts})
                      </h3>
                    </div>
                  </div>
                  <div className="divide-y">
                    {alerts.alerts.map((alert, idx) => (
                      <div key={idx} className="p-4 flex items-center justify-between hover:bg-slate-50">
                        <div className="flex items-center gap-3">
                          <div className={`w-2 h-2 rounded-full ${
                            alert.severity === 'critical' ? 'bg-red-500' : 
                            alert.severity === 'warning' ? 'bg-amber-500' : 'bg-blue-500'
                          }`} />
                          <div>
                            <p className="font-medium text-slate-800">{alert.title}</p>
                            <p className="text-sm text-slate-500">
                              {alert.count} cases • {formatCurrency(alert.total_amount)}
                            </p>
                          </div>
                        </div>
                        <Badge className={
                          alert.severity === 'critical' ? 'bg-red-100 text-red-700' :
                          alert.severity === 'warning' ? 'bg-amber-100 text-amber-700' : 'bg-blue-100 text-blue-700'
                        }>
                          {alert.severity}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Quick Aging Summary */}
              {aging?.summary && (
                <Card className="p-4" data-testid="aging-summary">
                  <h3 className="font-semibold text-slate-800 mb-4">Outstanding Aging Summary</h3>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 rounded-lg bg-emerald-50">
                      <p className="text-sm text-emerald-600 mb-1">0-30 Days</p>
                      <p className="text-xl font-bold text-emerald-700">{formatCurrency(aging.summary.bucket_0_30)}</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-amber-50">
                      <p className="text-sm text-amber-600 mb-1">31-60 Days</p>
                      <p className="text-xl font-bold text-amber-700">{formatCurrency(aging.summary.bucket_31_60)}</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-orange-50">
                      <p className="text-sm text-orange-600 mb-1">61-90 Days</p>
                      <p className="text-xl font-bold text-orange-700">{formatCurrency(aging.summary.bucket_61_90)}</p>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-red-50">
                      <p className="text-sm text-red-600 mb-1">90+ Days</p>
                      <p className="text-xl font-bold text-red-700">{formatCurrency(aging.summary.bucket_90_plus)}</p>
                    </div>
                  </div>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Revenue Cases Tab */}
          <TabsContent value="cases" data-testid="revenue-cases-tab">
            <div className="space-y-4">
              {/* Filters & Search */}
              <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-lg border">
                <form onSubmit={handleCasesSearch} className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      placeholder="Search customer, case ID, invoice..."
                      value={casesSearch}
                      onChange={(e) => setCasesSearch(e.target.value)}
                      className="pl-9"
                      data-testid="cases-search-input"
                    />
                  </div>
                </form>
                
                <Select value={casesInvoiceFilter} onValueChange={setCasesInvoiceFilter}>
                  <SelectTrigger className="w-36" data-testid="cases-invoice-filter">
                    <SelectValue placeholder="Invoice Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Invoice</SelectItem>
                    <SelectItem value="Not Eligible">Not Eligible</SelectItem>
                    <SelectItem value="Eligible">Eligible</SelectItem>
                    <SelectItem value="Drafted">Drafted</SelectItem>
                    <SelectItem value="Sent">Sent</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={casesCollectionFilter} onValueChange={setCasesCollectionFilter}>
                  <SelectTrigger className="w-40" data-testid="cases-collection-filter">
                    <SelectValue placeholder="Collection Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Collection</SelectItem>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="Committed">Committed</SelectItem>
                    <SelectItem value="Partially Collected">Partial</SelectItem>
                    <SelectItem value="Fully Collected">Fully Collected</SelectItem>
                    <SelectItem value="Disputed">Disputed</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={casesAgingFilter} onValueChange={setCasesAgingFilter}>
                  <SelectTrigger className="w-28" data-testid="cases-aging-filter">
                    <SelectValue placeholder="Aging" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="0-30">0-30d</SelectItem>
                    <SelectItem value="31-60">31-60d</SelectItem>
                    <SelectItem value="61-90">61-90d</SelectItem>
                    <SelectItem value="90+">90+d</SelectItem>
                  </SelectContent>
                </Select>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setCasesSearch('');
                    setCasesInvoiceFilter('');
                    setCasesCollectionFilter('');
                    setCasesAgingFilter('');
                  }}
                >
                  Clear
                </Button>
              </div>
              
              {/* Bulk Actions Bar */}
              {selectedCaseIds.length > 0 && (
                <div className="flex items-center gap-4 bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <span className="text-sm font-medium text-purple-700">
                    {selectedCaseIds.length} case(s) selected
                  </span>
                  <Button 
                    size="sm" 
                    onClick={() => setBulkUpdateOpen(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="bulk-update-btn"
                  >
                    <Edit size={14} className="mr-1" />
                    Bulk Update
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => setSelectedCaseIds([])}
                  >
                    Clear Selection
                  </Button>
                </div>
              )}
              
              {/* Cases Table */}
              <Card className="overflow-hidden" data-testid="cases-table">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-3 py-3 text-left">
                          <Checkbox
                            checked={selectedCaseIds.length === allCases.length && allCases.length > 0}
                            onCheckedChange={handleSelectAll}
                          />
                        </th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600">Customer</th>
                        <th className="px-3 py-3 text-left font-medium text-slate-600">Builder</th>
                        <th className="px-3 py-3 text-right font-medium text-slate-600">Brokerage</th>
                        <th className="px-3 py-3 text-center font-medium text-slate-600">Invoice</th>
                        <th className="px-3 py-3 text-center font-medium text-slate-600">Collection</th>
                        <th className="px-3 py-3 text-right font-medium text-slate-600">Collected</th>
                        <th className="px-3 py-3 text-right font-medium text-slate-600">Outstanding</th>
                        <th className="px-3 py-3 text-right font-medium text-slate-600">Days</th>
                        <th className="px-3 py-3 text-center font-medium text-slate-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {casesLoading ? (
                        <tr>
                          <td colSpan={10} className="px-4 py-8 text-center">
                            <RefreshCw className="animate-spin mx-auto text-purple-600" size={24} />
                          </td>
                        </tr>
                      ) : allCases.length === 0 ? (
                        <tr>
                          <td colSpan={10} className="px-4 py-8 text-center text-slate-500">
                            No brokerage cases found. Cases are auto-created when bookings reach "Registration Done" milestone.
                          </td>
                        </tr>
                      ) : (
                        allCases.map((c) => (
                          <tr 
                            key={c.case_id} 
                            className="hover:bg-slate-50"
                            data-testid={`case-row-${c.case_id}`}
                          >
                            <td className="px-3 py-3">
                              <Checkbox
                                checked={selectedCaseIds.includes(c.case_id)}
                                onCheckedChange={() => handleCaseSelect(c.case_id)}
                              />
                            </td>
                            <td className="px-3 py-3">
                              <div 
                                className="font-medium text-slate-800 cursor-pointer hover:text-purple-600"
                                onClick={() => openCaseDetail(c.case_id)}
                              >
                                {c.customer_name}
                              </div>
                              <div className="text-xs text-slate-500">{c.unit_details || c.case_id?.substring(0, 12)}</div>
                            </td>
                            <td className="px-3 py-3">
                              <div className="text-slate-700">{c.builder_name || 'Unknown'}</div>
                            </td>
                            <td className="px-3 py-3 text-right font-medium">{formatCurrency(c.brokerage_amount)}</td>
                            <td className="px-3 py-3 text-center">
                              <Badge className={INVOICE_STATUS_STYLES[c.invoice_status] || 'bg-slate-100'}>
                                {c.invoice_status}
                              </Badge>
                            </td>
                            <td className="px-3 py-3 text-center">
                              <Badge className={COLLECTION_STATUS_STYLES[c.collection_status] || 'bg-slate-100'}>
                                {c.collection_status}
                              </Badge>
                            </td>
                            <td className="px-3 py-3 text-right text-emerald-600">{formatCurrency(c.collected_amount)}</td>
                            <td className="px-3 py-3 text-right text-red-600 font-medium">{formatCurrency(c.outstanding_amount)}</td>
                            <td className="px-3 py-3 text-right">
                              <span className={getAgingColor(c.days_outstanding)}>{c.days_outstanding}d</span>
                            </td>
                            <td className="px-3 py-3">
                              <div className="flex items-center justify-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => openCaseDetail(c.case_id)}
                                  title="View Details"
                                >
                                  <Eye size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedCase(c);
                                    setStageUpdateData({});
                                    setStageUpdateOpen(true);
                                  }}
                                  title="Update Status"
                                >
                                  <Edit size={14} />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setSelectedCase(c);
                                    setPaymentData({ 
                                      amount: '', 
                                      payment_mode: '', 
                                      payment_reference: '', 
                                      payment_date: new Date().toISOString().split('T')[0], 
                                      remark: '' 
                                    });
                                    setPaymentModalOpen(true);
                                  }}
                                  title="Add Payment"
                                  className="text-emerald-600 hover:text-emerald-700"
                                >
                                  <Banknote size={14} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {casesTotal > casesLimit && (
                  <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
                    <span className="text-sm text-slate-600">
                      Showing {casesPage * casesLimit + 1} - {Math.min((casesPage + 1) * casesLimit, casesTotal)} of {casesTotal}
                    </span>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={casesPage === 0}
                        onClick={() => { setCasesPage(p => p - 1); fetchAllCases(casesPage - 1); }}
                      >
                        <ChevronLeft size={16} />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={(casesPage + 1) * casesLimit >= casesTotal}
                        onClick={() => { setCasesPage(p => p + 1); fetchAllCases(casesPage + 1); }}
                      >
                        <ChevronRight size={16} />
                      </Button>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          </TabsContent>

          {/* Aging Tab */}
          <TabsContent value="aging" data-testid="revenue-aging-tab">
            <div className="space-y-6">
              {/* Aging Summary Cards */}
              <div className="grid grid-cols-5 gap-4">
                <Card className="p-3 text-center bg-emerald-50 border-emerald-200">
                  <p className="text-xs text-emerald-600">0-30d</p>
                  <p className="text-lg font-bold text-emerald-700">{formatCurrency(aging?.summary?.bucket_0_30)}</p>
                </Card>
                <Card className="p-3 text-center bg-amber-50 border-amber-200">
                  <p className="text-xs text-amber-600">31-60d</p>
                  <p className="text-lg font-bold text-amber-700">{formatCurrency(aging?.summary?.bucket_31_60)}</p>
                </Card>
                <Card className="p-3 text-center bg-orange-50 border-orange-200">
                  <p className="text-xs text-orange-600">61-90d</p>
                  <p className="text-lg font-bold text-orange-700">{formatCurrency(aging?.summary?.bucket_61_90)}</p>
                </Card>
                <Card className="p-3 text-center bg-red-50 border-red-200">
                  <p className="text-xs text-red-600">90+d</p>
                  <p className="text-lg font-bold text-red-700">{formatCurrency(aging?.summary?.bucket_90_plus)}</p>
                </Card>
                <Card className="p-3 text-center bg-slate-100">
                  <p className="text-xs text-slate-600">Total</p>
                  <p className="text-lg font-bold text-slate-800">{formatCurrency(aging?.summary?.total_outstanding)}</p>
                </Card>
              </div>

              {/* Risk Summary */}
              <div className="flex gap-4">
                <Badge className="bg-red-100 text-red-700 px-3 py-1">
                  🔴 {aging?.summary?.high_risk_count || 0} High Risk Builders
                </Badge>
                <Badge className="bg-amber-100 text-amber-700 px-3 py-1">
                  🟡 {aging?.summary?.medium_risk_count || 0} Medium Risk Builders
                </Badge>
              </div>

              {/* Builder Aging Table */}
              <Card className="overflow-hidden" data-testid="aging-table">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-800">Builder-wise Outstanding</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">Builder</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Eligible</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Invoiced</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Collected</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Outstanding</th>
                        <th className="px-4 py-3 text-right font-medium text-emerald-600">0-30d</th>
                        <th className="px-4 py-3 text-right font-medium text-amber-600">31-60d</th>
                        <th className="px-4 py-3 text-right font-medium text-orange-600">61-90d</th>
                        <th className="px-4 py-3 text-right font-medium text-red-600">90+d</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Avg Days</th>
                        <th className="px-4 py-3 text-center font-medium text-slate-600">Risk</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {aging?.builders?.length === 0 ? (
                        <tr>
                          <td colSpan={11} className="px-4 py-8 text-center text-slate-500">
                            No outstanding brokerage cases found
                          </td>
                        </tr>
                      ) : (
                        aging?.builders?.map((builder) => (
                          <tr 
                            key={builder.builder_id}
                            className="hover:bg-slate-50 cursor-pointer"
                            onClick={() => fetchBuilderCases(builder.builder_id, builder.builder_name)}
                            data-testid={`aging-row-${builder.builder_id}`}
                          >
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-800">{builder.builder_name}</div>
                              <div className="text-xs text-slate-500">{builder.total_cases} cases</div>
                            </td>
                            <td className="px-4 py-3 text-right">{formatCurrency(builder.total_eligible)}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(builder.total_invoiced)}</td>
                            <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(builder.total_collected)}</td>
                            <td className="px-4 py-3 text-right font-medium">{formatCurrency(builder.total_outstanding)}</td>
                            <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(builder.bucket_0_30)}</td>
                            <td className="px-4 py-3 text-right text-amber-600">{formatCurrency(builder.bucket_31_60)}</td>
                            <td className="px-4 py-3 text-right text-orange-600">{formatCurrency(builder.bucket_61_90)}</td>
                            <td className="px-4 py-3 text-right text-red-600">{formatCurrency(builder.bucket_90_plus)}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={getAgingColor(builder.avg_days_outstanding)}>
                                {builder.avg_days_outstanding}d
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <Badge className={RISK_COLORS[builder.risk_level] || 'bg-slate-100 text-slate-600'}>
                                {builder.risk_level}
                              </Badge>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Performance Tab */}
          <TabsContent value="performance" data-testid="revenue-performance-tab">
            <div className="space-y-6">
              <Card className="overflow-hidden" data-testid="performance-table">
                <div className="p-4 border-b bg-slate-50">
                  <h3 className="font-semibold text-slate-800">BD Team Performance (Leaderboard)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">#</th>
                        <th className="px-4 py-3 text-left font-medium text-slate-600">BD Owner</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Assigned</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Total Brokerage</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Outstanding</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Collected MTD</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Avg Days</th>
                        <th className="px-4 py-3 text-right font-medium text-slate-600">Recovery %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {performance?.bd_performance?.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="px-4 py-8 text-center text-slate-500">
                            No BD performance data available
                          </td>
                        </tr>
                      ) : (
                        performance?.bd_performance?.map((bd, idx) => (
                          <tr key={bd.bd_user_id} className="hover:bg-slate-50">
                            <td className="px-4 py-3 text-slate-500">{idx + 1}</td>
                            <td className="px-4 py-3">
                              <div className="font-medium text-slate-800">{bd.bd_user_name}</div>
                            </td>
                            <td className="px-4 py-3 text-right">{bd.assigned_cases}</td>
                            <td className="px-4 py-3 text-right">{formatCurrency(bd.total_brokerage)}</td>
                            <td className="px-4 py-3 text-right text-red-600">{formatCurrency(bd.total_outstanding)}</td>
                            <td className="px-4 py-3 text-right text-emerald-600">{formatCurrency(bd.collected_mtd)}</td>
                            <td className="px-4 py-3 text-right">
                              <span className={getAgingColor(bd.avg_days_to_collect)}>
                                {bd.avg_days_to_collect}d
                              </span>
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={bd.recovery_pct >= 80 ? 'text-emerald-600 font-medium' : bd.recovery_pct >= 50 ? 'text-amber-600' : 'text-red-600'}>
                                {bd.recovery_pct}%
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Builder Cases Modal */}
      <Dialog open={!!selectedBuilder} onOpenChange={() => setSelectedBuilder(null)}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={20} />
              {selectedBuilder?.builder_name} - Brokerage Cases
            </DialogTitle>
            <DialogDescription>
              {builderCases?.summary?.total_cases || 0} cases • 
              Outstanding: {formatCurrency(builderCases?.summary?.total_outstanding)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-auto">
            {casesLoading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="animate-spin text-purple-600" size={24} />
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-slate-600">Customer</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Amount</th>
                    <th className="px-3 py-2 text-center font-medium text-slate-600">Invoice</th>
                    <th className="px-3 py-2 text-center font-medium text-slate-600">Collection</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Collected</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Outstanding</th>
                    <th className="px-3 py-2 text-right font-medium text-slate-600">Days</th>
                    <th className="px-3 py-2 text-center font-medium text-slate-600">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {builderCases?.cases?.map((c) => (
                    <tr key={c.case_id} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="font-medium text-slate-800">{c.customer_name}</div>
                        <div className="text-xs text-slate-500">{c.unit_details}</div>
                      </td>
                      <td className="px-3 py-2 text-right">{formatCurrency(c.brokerage_amount)}</td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={INVOICE_STATUS_STYLES[c.invoice_status] || 'bg-slate-100'}>
                          {c.invoice_status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Badge className={COLLECTION_STATUS_STYLES[c.collection_status] || 'bg-slate-100'}>
                          {c.collection_status}
                        </Badge>
                      </td>
                      <td className="px-3 py-2 text-right text-emerald-600">{formatCurrency(c.collected_amount)}</td>
                      <td className="px-3 py-2 text-right text-red-600">{formatCurrency(c.outstanding_amount)}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={getAgingColor(c.days_outstanding)}>{c.days_outstanding}d</span>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCase(c);
                            setStageUpdateData({});
                            setStageUpdateOpen(true);
                          }}
                        >
                          Update
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Stage Update Modal */}
      <Dialog open={stageUpdateOpen} onOpenChange={setStageUpdateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Update Case Stage</DialogTitle>
            <DialogDescription>
              {selectedCase?.customer_name} - {formatCurrency(selectedCase?.brokerage_amount)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Invoice Status */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Invoice Status</label>
              <Select 
                value={stageUpdateData.invoice_status || selectedCase?.invoice_status}
                onValueChange={(v) => setStageUpdateData({...stageUpdateData, invoice_status: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Not Eligible">Not Eligible</SelectItem>
                  <SelectItem value="Eligible">Eligible</SelectItem>
                  <SelectItem value="Drafted">Drafted</SelectItem>
                  <SelectItem value="Sent">Sent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Invoice Number & Date (if Sent) */}
            {(stageUpdateData.invoice_status === 'Sent' || stageUpdateData.invoice_status === 'Drafted') && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Invoice Number</label>
                  <Input
                    value={stageUpdateData.invoice_number || ''}
                    onChange={(e) => setStageUpdateData({...stageUpdateData, invoice_number: e.target.value})}
                    placeholder="INV-2024-001"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Invoice Date</label>
                  <Input
                    type="date"
                    value={stageUpdateData.invoice_date || ''}
                    onChange={(e) => setStageUpdateData({...stageUpdateData, invoice_date: e.target.value})}
                  />
                </div>
              </>
            )}
            
            {/* Collection Status */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Collection Status</label>
              <Select 
                value={stageUpdateData.collection_status || selectedCase?.collection_status}
                onValueChange={(v) => setStageUpdateData({...stageUpdateData, collection_status: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Not Started">Not Started</SelectItem>
                  <SelectItem value="Committed">Committed</SelectItem>
                  <SelectItem value="Partially Collected">Partially Collected</SelectItem>
                  <SelectItem value="Fully Collected">Fully Collected</SelectItem>
                  <SelectItem value="Disputed">Disputed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Collected Amount */}
            {(stageUpdateData.collection_status === 'Partially Collected' || 
              stageUpdateData.collection_status === 'Fully Collected') && (
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Collected Amount (Brokerage: {formatCurrency(selectedCase?.brokerage_amount)})
                </label>
                <Input
                  type="number"
                  value={stageUpdateData.collected_amount || ''}
                  onChange={(e) => setStageUpdateData({...stageUpdateData, collected_amount: parseFloat(e.target.value) || 0})}
                  placeholder="Enter amount"
                />
              </div>
            )}
            
            {/* Payment Reference */}
            {stageUpdateData.collected_amount > 0 && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Payment Mode</label>
                  <Select 
                    value={stageUpdateData.payment_mode || ''}
                    onValueChange={(v) => setStageUpdateData({...stageUpdateData, payment_mode: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select mode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NEFT">NEFT</SelectItem>
                      <SelectItem value="RTGS">RTGS</SelectItem>
                      <SelectItem value="Cheque">Cheque</SelectItem>
                      <SelectItem value="Online">Online</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Payment Reference</label>
                  <Input
                    value={stageUpdateData.payment_reference || ''}
                    onChange={(e) => setStageUpdateData({...stageUpdateData, payment_reference: e.target.value})}
                    placeholder="Transaction ID / Cheque No."
                  />
                </div>
              </>
            )}
            
            {/* Remark */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Remark (Optional)</label>
              <Input
                value={stageUpdateData.remark || ''}
                onChange={(e) => setStageUpdateData({...stageUpdateData, remark: e.target.value})}
                placeholder="Add a note..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setStageUpdateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleStageUpdate}>
              Update Case
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Case Detail Modal */}
      <Dialog open={caseDetailOpen} onOpenChange={setCaseDetailOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText size={20} />
              Case Details
            </DialogTitle>
            <DialogDescription>
              {caseDetail?.case?.customer_name} - {formatCurrency(caseDetail?.case?.brokerage_amount)}
            </DialogDescription>
          </DialogHeader>
          
          {caseDetailLoading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-purple-600" size={24} />
            </div>
          ) : caseDetail?.case && (
            <div className="flex-1 overflow-auto space-y-4">
              {/* Case Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-xs text-slate-500">Brokerage</p>
                  <p className="font-bold text-slate-800">{formatCurrency(caseDetail.case.brokerage_amount)}</p>
                </div>
                <div className="bg-emerald-50 p-3 rounded-lg">
                  <p className="text-xs text-emerald-600">Collected</p>
                  <p className="font-bold text-emerald-700">{formatCurrency(caseDetail.case.collected_amount)}</p>
                </div>
                <div className="bg-red-50 p-3 rounded-lg">
                  <p className="text-xs text-red-600">Outstanding</p>
                  <p className="font-bold text-red-700">{formatCurrency(caseDetail.case.outstanding_amount)}</p>
                </div>
                <div className="bg-amber-50 p-3 rounded-lg">
                  <p className="text-xs text-amber-600">Days Outstanding</p>
                  <p className="font-bold text-amber-700">{caseDetail.case.days_outstanding}d</p>
                </div>
              </div>
              
              {/* Status Badges */}
              <div className="flex gap-2 flex-wrap">
                <Badge className={INVOICE_STATUS_STYLES[caseDetail.case.invoice_status]}>
                  Invoice: {caseDetail.case.invoice_status}
                </Badge>
                <Badge className={COLLECTION_STATUS_STYLES[caseDetail.case.collection_status]}>
                  Collection: {caseDetail.case.collection_status}
                </Badge>
                {caseDetail.case.invoice_number && (
                  <Badge variant="outline">Invoice #: {caseDetail.case.invoice_number}</Badge>
                )}
              </div>
              
              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm border-t pt-4">
                <div>
                  <p className="text-slate-500">Builder</p>
                  <p className="font-medium">{caseDetail.builder?.builder_display_name || caseDetail.builder?.builder_name || 'Unknown'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Customer Phone</p>
                  <p className="font-medium">{caseDetail.case.customer_phone || caseDetail.booking?.customer_phone || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Unit Details</p>
                  <p className="font-medium">{caseDetail.case.unit_details || caseDetail.booking?.unit_details || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Brokerage Rate</p>
                  <p className="font-medium">{caseDetail.case.brokerage_rate_percent ? `${caseDetail.case.brokerage_rate_percent}%` : '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Invoice Date</p>
                  <p className="font-medium">{caseDetail.case.invoice_date || '-'}</p>
                </div>
                <div>
                  <p className="text-slate-500">Due Date</p>
                  <p className="font-medium">{caseDetail.case.invoice_due_date || '-'}</p>
                </div>
              </div>
              
              {/* Payment History */}
              {caseDetail.payment_history?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                    <Banknote size={16} />
                    Payment History
                  </h4>
                  <div className="space-y-2">
                    {caseDetail.payment_history.map((p, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-emerald-50 p-3 rounded-lg">
                        <div>
                          <p className="font-medium text-emerald-700">{formatCurrency(p.amount)}</p>
                          <p className="text-xs text-emerald-600">{p.payment_mode} • {p.payment_reference}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-slate-600">{p.payment_date}</p>
                          <p className="text-xs text-slate-500">by {p.recorded_by_name}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Stage History */}
              {caseDetail.stage_history?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-medium text-slate-800 mb-3 flex items-center gap-2">
                    <History size={16} />
                    Stage History
                  </h4>
                  <div className="space-y-2 max-h-40 overflow-auto">
                    {caseDetail.stage_history.slice().reverse().map((s, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-slate-50 p-2 rounded text-sm">
                        <div>
                          {s.invoice_status && <Badge size="sm" className="mr-1">{s.invoice_status}</Badge>}
                          {s.collection_status && <Badge size="sm" className="mr-1">{s.collection_status}</Badge>}
                          {s.payment_amount && <span className="text-emerald-600 ml-1">+{formatCurrency(s.payment_amount)}</span>}
                          {s.remark && <span className="text-slate-500 ml-2">"{s.remark}"</span>}
                        </div>
                        <span className="text-xs text-slate-400">{new Date(s.timestamp).toLocaleDateString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter className="border-t pt-4">
            <Button variant="outline" onClick={() => setCaseDetailOpen(false)}>
              Close
            </Button>
            <Button onClick={() => {
              setCaseDetailOpen(false);
              setSelectedCase(caseDetail?.case);
              setPaymentData({ 
                amount: '', 
                payment_mode: '', 
                payment_reference: '', 
                payment_date: new Date().toISOString().split('T')[0], 
                remark: '' 
              });
              setPaymentModalOpen(true);
            }}>
              <Banknote size={14} className="mr-1" />
              Add Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Update Modal */}
      <Dialog open={bulkUpdateOpen} onOpenChange={setBulkUpdateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Bulk Update Cases</DialogTitle>
            <DialogDescription>
              Update {selectedCaseIds.length} selected case(s)
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Invoice Status</label>
              <Select 
                value={bulkUpdateData.invoice_status || 'keep'}
                onValueChange={(v) => setBulkUpdateData({...bulkUpdateData, invoice_status: v === 'keep' ? undefined : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Keep Current" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Keep Current</SelectItem>
                  <SelectItem value="Eligible">Eligible</SelectItem>
                  <SelectItem value="Drafted">Drafted</SelectItem>
                  <SelectItem value="Sent">Sent</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {bulkUpdateData.invoice_status === 'Sent' && (
              <>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Invoice Number</label>
                  <Input
                    value={bulkUpdateData.invoice_number || ''}
                    onChange={(e) => setBulkUpdateData({...bulkUpdateData, invoice_number: e.target.value})}
                    placeholder="INV-2024-001"
                  />
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-700 mb-1 block">Invoice Date *</label>
                  <Input
                    type="date"
                    value={bulkUpdateData.invoice_date || ''}
                    onChange={(e) => setBulkUpdateData({...bulkUpdateData, invoice_date: e.target.value})}
                  />
                </div>
              </>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Collection Status</label>
              <Select 
                value={bulkUpdateData.collection_status || 'keep'}
                onValueChange={(v) => setBulkUpdateData({...bulkUpdateData, collection_status: v === 'keep' ? undefined : v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Keep Current" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="keep">Keep Current</SelectItem>
                  <SelectItem value="Committed">Committed</SelectItem>
                  <SelectItem value="Disputed">Disputed</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Remark</label>
              <Input
                value={bulkUpdateData.remark || ''}
                onChange={(e) => setBulkUpdateData({...bulkUpdateData, remark: e.target.value})}
                placeholder="Add a note..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setBulkUpdateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleBulkUpdate} disabled={bulkUpdateLoading}>
              {bulkUpdateLoading ? <RefreshCw size={14} className="mr-1 animate-spin" /> : null}
              Update {selectedCaseIds.length} Cases
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Partial Payment Modal */}
      <Dialog open={paymentModalOpen} onOpenChange={setPaymentModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Banknote size={20} className="text-emerald-600" />
              Record Payment
            </DialogTitle>
            <DialogDescription>
              {selectedCase?.customer_name} • Outstanding: {formatCurrency(selectedCase?.outstanding_amount)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Amount Received *</label>
              <Input
                type="number"
                value={paymentData.amount}
                onChange={(e) => setPaymentData({...paymentData, amount: e.target.value})}
                placeholder="Enter amount"
              />
              {selectedCase?.outstanding_amount && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="p-0 h-auto text-xs"
                  onClick={() => setPaymentData({...paymentData, amount: selectedCase.outstanding_amount.toString()})}
                >
                  Full amount: {formatCurrency(selectedCase.outstanding_amount)}
                </Button>
              )}
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Payment Mode *</label>
              <Select 
                value={paymentData.payment_mode}
                onValueChange={(v) => setPaymentData({...paymentData, payment_mode: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="NEFT">NEFT</SelectItem>
                  <SelectItem value="RTGS">RTGS</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Online">Online</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Payment Reference *</label>
              <Input
                value={paymentData.payment_reference}
                onChange={(e) => setPaymentData({...paymentData, payment_reference: e.target.value})}
                placeholder="Transaction ID / Cheque No."
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Payment Date *</label>
              <Input
                type="date"
                value={paymentData.payment_date}
                onChange={(e) => setPaymentData({...paymentData, payment_date: e.target.value})}
              />
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Remark</label>
              <Input
                value={paymentData.remark}
                onChange={(e) => setPaymentData({...paymentData, remark: e.target.value})}
                placeholder="Add a note..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handlePartialPayment}
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={!paymentData.amount || !paymentData.payment_mode || !paymentData.payment_reference}
            >
              <CheckCircle size={14} className="mr-1" />
              Record Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BDRevenuePipeline;
